import React, { useEffect, useState } from 'react';

import { Button, Icons, Select } from '@components/common';

import { useQueryPortalTasksLinkTypes } from '@hooks/portal/tasks/useQueryPortalTasksLinkTypes';
import { useQueryPortalTasksToLink } from '@hooks/portal/tasks/useQueryPortalTasksToLink';

import { portalTasks as portalTasksSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setLinkedTask } from '@slices/portalTasks';

import { TNameTitle } from '@interfaces/common/common';
import { IPortalTaskToLinkDTO } from '@interfaces/portal/dto/taskManagementDTO';

import colors from '@constants/colors';
import { linkedTasksStatusNames, linkedTasksStatusTitles } from '@constants/common';
import {
	finishToFinishDefinitionText,
	finishToStartDefinitionText,
	startToFinishDefinitionText,
	startToStartDefinitionText,
	taskLinkTypesTitles,
} from './constants';

import LinkedTasksList from '../LinkedTasksList';
import { validateTaskLink } from './utils';

import styles from './LinkTasksForm.module.scss';

interface ILinkTasksFormProps {
	projectId?: string;
	taskId?: string | null;
	predecessorTaskStartDate?: number | null;
	predecessorTaskEndDate?: number | null;
	isViewMode: boolean;
}

const LinkTasksForm = ({
	projectId,
	taskId,
	predecessorTaskStartDate,
	predecessorTaskEndDate,
	isViewMode,
}: ILinkTasksFormProps) => {
	const dispatch = useAppDispatch();

	const { linkedTasks } = useAppSelector(portalTasksSelector);

	const [selectedLinkType, setSelectedLinkType] = useState<TNameTitle | null>(null);
	const [selectedTaskToLink, setSelectedTaskToLink] = useState<IPortalTaskToLinkDTO | null>(null);
	const [taskLinkErrorMessage, setTaskLinkErrorMessage] = useState<string | null>(null);
	const [taskLinkWarningMessage, setTaskLinkWarningMessage] = useState<string | null>(null);

	const { portalTasksLinkTypes } = useQueryPortalTasksLinkTypes();
	const { portalTasksToLink } = useQueryPortalTasksToLink({
		projectId: !isViewMode ? projectId : undefined,
		taskId,
	});

	const onLinkTypeChange = (option: TNameTitle) => {
		setSelectedLinkType(option);
	};

	const onTaskToLinkChange = (option: IPortalTaskToLinkDTO) => {
		setSelectedTaskToLink(option);
	};

	const isLinkDisabled = !(selectedLinkType && selectedTaskToLink) || !!taskLinkErrorMessage;

	const sortedLinkTypes = portalTasksLinkTypes.sort((a, b) => {
		if (!a?.weight) return 1;

		if (!b?.weight) return -1;

		return a.weight - b.weight;
	});

	const onLink = () => {
		if (!selectedTaskToLink || !selectedLinkType) return;

		const linkedTask = {
			id: selectedTaskToLink.id,
			status: {
				name: taskLinkWarningMessage ? linkedTasksStatusNames.inactive : linkedTasksStatusNames.active,
				title: taskLinkWarningMessage ? linkedTasksStatusTitles.inactive : linkedTasksStatusTitles.active,
			},
			taskId: selectedTaskToLink.taskId,
			title: selectedTaskToLink.title,
			startDate: selectedTaskToLink?.startDate,
			endDate: selectedTaskToLink?.endDate,
			type: {
				name: selectedLinkType.name,
				title: selectedLinkType.title,
			},
			isPredecessor: false,
		};

		dispatch(setLinkedTask({ linkedTask }));

		setSelectedTaskToLink(null);
		setSelectedLinkType(null);
		setTaskLinkWarningMessage(null);
	};

	useEffect(() => {
		if (!selectedLinkType || !selectedTaskToLink) return;

		validateTaskLink({
			linkType: selectedLinkType.name,
			taskToLink: selectedTaskToLink,
			linkedTasks,
			setTaskLinkErrorMessage,
			setTaskLinkWarningMessage,
			predecessorTaskStartDate,
			predecessorTaskEndDate,
		});
	}, [
		selectedLinkType,
		selectedTaskToLink,
		linkedTasks,
		predecessorTaskStartDate,
		predecessorTaskEndDate,
	]);

	return (
		<>
			{!isViewMode
				&& <div className={styles.linkTaskInputsContainer}>
					<div className={styles.linkType}>
						<Select
							handleChange={onLinkTypeChange}
							isWithoutUseForms
							label='Link Type'
							name='linkType'
							options={sortedLinkTypes}
							placeholder='Choose link type'
							tooltipContent={
								<>
									<b>{taskLinkTypesTitles.finishToStart}</b> {finishToStartDefinitionText}<br />
									<b>{taskLinkTypesTitles.finishToFinish}</b> {finishToFinishDefinitionText}<br />
									<b>{taskLinkTypesTitles.startToStart}</b> {startToStartDefinitionText}<br />
									<b>{taskLinkTypesTitles.startToFinish}</b> {startToFinishDefinitionText}
								</>
							}
							tooltipMaxWidth={290}
							tooltipPlacement='right'
							value={selectedLinkType}
						/>
					</div>
					<div className={styles.taskToLink}>
						<Select
							handleChange={onTaskToLinkChange}
							isWithoutUseForms
							label='Task to link'
							name='taskToLink'
							options={portalTasksToLink}
							placeholder='Choose task to link'
							value={selectedTaskToLink}
						/>
					</div>
					<div className={styles.linkButton}>
						<Button
							disabled={isLinkDisabled}
							icon={Icons.link({
								color: isLinkDisabled ? colors.gray40 : undefined,
							})}
							onClick={onLink}
							text='Link'
							withoutBackground
						/>
					</div>
				</div>
			}
			{taskLinkErrorMessage && <div className={styles.errorMessage}>{taskLinkErrorMessage}</div>}
			{taskLinkWarningMessage && <div className={styles.warningMessage}>{taskLinkWarningMessage}</div>}
			<div className={styles.linkedTasksList}>
				<LinkedTasksList isViewMode={isViewMode} />
			</div>
		</>
	);
};

export default LinkTasksForm;
